import type { Ref, RefCallback } from "react";

/** 주소를 달기 전 그림 자리(1×1 투명 GIF) — 주소 없는 `<img>` 는 대체 글을 글자로 그린다 */
export const BLANK_IMAGE =
  "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7";

let pageLoaded: Promise<void> | undefined;

function whenPageLoaded(): Promise<void> {
  pageLoaded ??= new Promise((resolve) => {
    if (document.readyState === "complete") resolve();
    else window.addEventListener("load", () => resolve(), { once: true });
  });
  return pageLoaded;
}

function isOnScreen(element: Element): boolean {
  const rect = element.getBoundingClientRect();
  return (
    rect.bottom > 0 &&
    rect.right > 0 &&
    rect.top < window.innerHeight &&
    rect.left < window.innerWidth
  );
}

function assignRef<T>(ref: Ref<T> | undefined, value: T | null) {
  if (typeof ref === "function") ref(value);
  else if (ref) ref.current = value;
}

/**
 * 화면 밖 미디어의 주소는 페이지 load 뒤에 단다(site-media-delivery 「첫 화면 우선순위」) — 첫 화면 그림·글꼴이 먼저 대역을 쓰고,
 * 그 뒤 거리 판단은 브라우저 지연 로드(`loading="lazy"`)가 한다. 첫 화면에 걸친 것은 바로 단다. 돌려주는 함수로 취소한다.
 */
function attachAfterPageLoad(element: Element, attach: () => void): () => void {
  if (isOnScreen(element)) {
    attach();
    return () => {};
  }
  let cancelled = false;
  void whenPageLoaded().then(() => {
    if (!cancelled) attach();
  });
  return () => {
    cancelled = true;
  };
}

/** 그림 주소를 바꿀 때만 단다 — 같은 주소를 다시 달면 브라우저가 그림을 다시 고른다 */
function setImageSources(
  image: HTMLImageElement,
  src: string,
  srcSet?: string
) {
  if (srcSet !== undefined && image.getAttribute("srcset") !== srcSet)
    image.srcset = srcSet;
  if (image.getAttribute("src") !== src) image.src = src;
}

/**
 * 화면 밖 그림의 주소를 페이지 load 뒤에 다는 ref(`attachAfterPageLoad`) — 바깥 ref 에도 요소를 넘긴다.
 * `deferred` 가 거짓(첫 화면 그림)이면 바깥 ref 만 단다. 주소는 렌더한 `src`·`srcSet` 대신 이 ref 가 단다.
 */
export function useAfterPageLoadImage(
  outerRef: Ref<HTMLImageElement> | undefined,
  src: string,
  srcSet?: string,
  deferred = true
): RefCallback<HTMLImageElement> {
  return (image) => {
    assignRef(outerRef, image);
    if (!image || !deferred) return undefined;
    const cancel = attachAfterPageLoad(image, () =>
      setImageSources(image, src, srcSet)
    );
    return () => {
      cancel();
      assignRef(outerRef, null);
    };
  };
}
